/**
 * Export content to Excel (.xlsx) format.
 *
 * Converts structured output or chat messages into an Excel workbook
 * using the `xlsx` library (SheetJS).
 */

import * as XLSX from "xlsx";
import { AI_DISCLAIMER } from "@cvx/lib/aiDisclaimer";
import type { ExportContent } from "./exportToWord";

/**
 * Prepend the AI disclaimer (row 1) and a spacer (row 2) to a sheet's rows.
 *
 * This pushes headers to row 3. Accepted trade-off: these workbooks are read by
 * practitioners, and CSV — which has to stay machine-parseable — is deliberately
 * left undisclaimed instead.
 *
 * Applied at every `aoa_to_sheet` call site, so a new export branch that skips
 * it ships an undisclaimed workbook.
 */
function withDisclaimerRows<T>(data: T[][]): (T | string)[][] {
  return [[AI_DISCLAIMER], [], ...data];
}

/** Narrow a dynamic value to a plain object without discarding type safety. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Flatten a nested object into rows of [key, value] pairs */
function flattenObject(
  obj: Record<string, unknown>,
  prefix = "",
): Array<[string, string]> {
  const rows: Array<[string, string]> = [];

  for (const [key, value] of Object.entries(obj)) {
    const label =
      (prefix ? `${prefix} > ` : "") +
      key
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim();

    if (value === null || value === undefined) continue;

    if (Array.isArray(value)) {
      if (value.length === 0) {
        rows.push([label, "(empty)"]);
      } else if (typeof value[0] === "object" && value[0] !== null) {
        rows.push([label, `${value.length} items`]);
        for (let i = 0; i < value.length; i++) {
          const itemRows = flattenObject(
            value[i] as Record<string, unknown>,
            `${label} [${i + 1}]`,
          );
          rows.push(...itemRows);
        }
      } else {
        rows.push([label, value.map(String).join(", ")]);
      }
    } else if (isRecord(value)) {
      rows.push([label, ""]);
      const nested = flattenObject(value, label);
      rows.push(...nested);
    } else {
      rows.push([label, String(value)]);
    }
  }

  return rows;
}

function formatCellValue(v: unknown): string | number | Date {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v instanceof Date) return v;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/**
 * Excel sheet names are capped at 31 chars, cannot contain `\ / ? * [ ] :`,
 * and must be unique within a workbook. Sanitise, truncate, and de-duplicate
 * (appending ` (2)`, ` (3)`, …) so per-document sheets never collide.
 */
function makeUniqueSheetName(raw: string, used: Set<string>): string {
  const base =
    (raw || "Sheet")
      .replace(/[\\/?*[\]:]/g, " ")
      .trim()
      .slice(0, 31) || "Sheet";
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  for (let i = 2; ; i++) {
    const suffix = ` (${i})`;
    const candidate = base.slice(0, 31 - suffix.length) + suffix;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
  }
}

export function exportToExcel(content: ExportContent): Blob {
  const wb = XLSX.utils.book_new();

  if (content.sheets && content.sheets.length > 0) {
    const usedNames = new Set<string>();
    for (const sheet of content.sheets) {
      if (sheet.omitFromExcel) continue;
      const sheetName = makeUniqueSheetName(sheet.name, usedNames);
      const data: (string | number | Date)[][] = [
        sheet.headers,
        ...sheet.rows.map((row) => row.map((cell) => formatCellValue(cell))),
      ];
      const ws = XLSX.utils.aoa_to_sheet(withDisclaimerRows(data), {
        cellDates: true,
      });

      const maxWidths = sheet.headers.map((h, colIdx) => {
        const max = Math.max(
          h.length,
          ...sheet.rows.map((row) => String(row[colIdx] ?? "").length),
        );
        return Math.min(Math.max(max + 2, 12), 60);
      });
      ws["!cols"] = maxWidths.map((wch) => ({ wch }));

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    }
  } else if (content.messages) {
    const data = [
      ["Speaker", "Message", "Timestamp"],
      ...content.messages.map((m) => [
        m.role === "user" ? "User" : "Morgan AI",
        m.content,
        "",
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(withDisclaimerRows(data));
    ws["!cols"] = [{ wch: 12 }, { wch: 100 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, ws, "Chat");
  } else if (content.output) {
    const rows = flattenObject(content.output);
    const data = [["Field", "Value"], ...rows];
    const ws = XLSX.utils.aoa_to_sheet(withDisclaimerRows(data));

    ws["!cols"] = [{ wch: 40 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, ws, content.title.slice(0, 31));

    for (const [key, value] of Object.entries(content.output)) {
      if (!Array.isArray(value) || value.length === 0) continue;
      const first: unknown = value[0];
      if (!isRecord(first)) continue;

      const sheetName = key
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .trim()
        .slice(0, 31);

      const headers = Object.keys(first).map((h) =>
        h
          .replace(/([A-Z])/g, " $1")
          .replace(/_/g, " ")
          .replace(/^\w/, (c) => c.toUpperCase())
          .trim(),
      );
      const rawHeaders = Object.keys(first);

      const sheetData = [
        headers,
        ...value.map((item: unknown) =>
          rawHeaders.map((h) => {
            const v = (item as Record<string, unknown>)[h];
            return formatCellValue(v);
          }),
        ),
      ];

      const ws2 = XLSX.utils.aoa_to_sheet(withDisclaimerRows(sheetData));
      ws2["!cols"] = headers.map(() => ({ wch: 25 }));
      XLSX.utils.book_append_sheet(wb, ws2, sheetName);
    }
  } else if (content.markdown) {
    const lines = content.markdown.split("\n").filter((l) => l.trim());
    const data = [["Content"], ...lines.map((l) => [l])];
    const ws = XLSX.utils.aoa_to_sheet(withDisclaimerRows(data));
    ws["!cols"] = [{ wch: 120 }];
    XLSX.utils.book_append_sheet(wb, ws, content.title.slice(0, 31));
  } else {
    const ws = XLSX.utils.aoa_to_sheet([["No content available"]]);
    XLSX.utils.book_append_sheet(wb, ws, "Export");
  }

  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}
